import { EditOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText, formatDateTime } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  NotificationsResponse,
  SiteConfigurationListResponse,
  SiteConfigurationRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

/** SiteConfiguration::CONFIG_TYPE_*. */
const TYPE_STRING = 0;
const TYPE_NUMBER = 1;
const TYPE_DATETIME = 2;

interface ConfigFormValues {
  denomination: string;
  description: string;
  config_type: number;
  string_value: string;
  number_value: number | null;
  datetime_value: dayjs.Dayjs | null;
}

/**
 * Sidebar entry "site_configurations" → /configuration/configurations.
 *
 * The site-wide settings, the web master's own tier. Most of the bounds the
 * other settings screens offer come from here: how far a speaker may look
 * ahead, how long a congregation may hold a speaker, how many months of past
 * events stay visible.
 *
 * A row carries three value columns and uses exactly one of them, decided by
 * its type — SiteConfiguration#getConfigValue reads that one and ignores the
 * rest. There is no create or delete: the entries are seeded, because the code
 * looks them up by `config_id`.
 */
export function SiteConfigurationPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ConfigFormValues>();

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<SiteConfigurationRow | null>(null);
  const editedType = Form.useWatch("config_type", form);

  const configurations = useQuery({
    queryKey: ["configuration"],
    queryFn: () => apiFetch<SiteConfigurationListResponse>("/configuration"),
  });

  const save = useMutation({
    mutationFn: (values: ConfigFormValues) => {
      // Only the value column the chosen type actually uses is sent. update()
      // drops nil parameters, so the other two keep whatever they held — which
      // is what makes switching a type back and forth harmless.
      const body: Record<string, unknown> = {
        denomination: values.denomination,
        description: values.description,
        config_type: values.config_type,
      };

      if (values.config_type === TYPE_STRING) body.string_value = values.string_value ?? "";
      if (values.config_type === TYPE_NUMBER) body.number_value = values.number_value;
      if (values.config_type === TYPE_DATETIME) {
        body.datetime_value = values.datetime_value ? values.datetime_value.toISOString() : null;
      }

      return apiFetch<NotificationsResponse>(`/configuration/${editing!.id}`, {
        method: "PUT",
        body,
      });
    },
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["configuration"] });
      // Nearly every other settings screen reads its bounds from here.
      void queryClient.invalidateQueries({ queryKey: ["speaker", "settings"] });
      void queryClient.invalidateQueries({ queryKey: ["circumscription", "settings"] });
      void queryClient.invalidateQueries({ queryKey: ["congregation", "settings"] });
      void queryClient.invalidateQueries({ queryKey: ["main", "settings"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const openEditor = useCallback(
    (row: SiteConfigurationRow) => {
      setEditing(row);
      form.resetFields();
      form.setFieldsValue({
        denomination: row.denomination ?? "",
        description: row.description ?? "",
        config_type: row.config_type,
        string_value: row.string_value ?? "",
        number_value: row.number_value,
        datetime_value: row.datetime_value ? dayjs(row.datetime_value) : null,
      });
    },
    [form],
  );

  const types = useMemo(
    () => configurations.data?.config_types ?? [],
    [configurations.data],
  );

  const labelOfType = useCallback(
    (value: number) => types.find((type) => type.value === value)?.label ?? String(value),
    [types],
  );

  /** The one value that counts for a row, as text. */
  const valueOf = useCallback((row: SiteConfigurationRow) => {
    if (row.config_type === TYPE_NUMBER) return row.number_value?.toString() ?? "";
    if (row.config_type === TYPE_DATETIME) return formatDateTime(row.datetime_value);
    return row.string_value ?? "";
  }, []);

  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const all = configurations.data?.data ?? [];
    if (!needle) return all;

    return all.filter((row) =>
      [row.config_id, row.denomination, row.description, valueOf(row)].some((field) =>
        (field ?? "").toLocaleLowerCase().includes(needle),
      ),
    );
  }, [configurations.data, search, valueOf]);

  const columns = useMemo<ExportableColumn<SiteConfigurationRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.site_configuration.config_id"),
        dataIndex: "config_id",
        width: 280,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.config_id, b.config_id),
        render: (value: string | null) => <Typography.Text code>{value}</Typography.Text>,
      },
      {
        title: t("activerecord.attributes.site_configuration.denomination"),
        dataIndex: "denomination",
        width: 220,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.site_configuration.config_type"),
        dataIndex: "config_type",
        width: 120,
        render: (value: number) => labelOfType(value),
        // The type is a number in the row and a word on the screen.
        exportValue: (row) => labelOfType(row.config_type),
      },
      {
        title: t("site_configuration.string_value"),
        key: "value",
        width: 220,
        ellipsis: true,
        render: (_value, row) => valueOf(row),
        // The value lives in one of three columns depending on the type, so the
        // export cannot read it off a dataIndex — and a column without one is
        // skipped, which left the file without the actual settings.
        exportValue: (row) => valueOf(row),
      },
      {
        title: t("activerecord.attributes.site_configuration.description"),
        dataIndex: "description",
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 70,
        render: (_value, row) => (
          <Tooltip title={t("form.update")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={save.isPending}
              onClick={() => openEditor(row)}
            />
          </Tooltip>
        ),
      },
    ],
    [t, labelOfType, valueOf, openEditor, save.isPending],
  );

  return (
    <Card title={t("navigation.site_configurations")}>
      <Input.Search
        allowClear
        placeholder={t("filter.search")}
        onChange={(event) => setSearch(event.target.value)}
        style={{ width: 320, marginBottom: 16 }}
      />

      <DataTable<SiteConfigurationRow>
        columns={columns}
        rows={rows}
        exportAs={t("navigation.site_configurations")}
        loading={configurations.isPending || save.isPending}
        error={configurations.error}
      />

      <Modal
        open={editing !== null}
        title={editing?.config_id}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<ConfigFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item
            name="denomination"
            label={t("activerecord.attributes.site_configuration.denomination")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("activerecord.attributes.site_configuration.description")}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="config_type"
            label={t("activerecord.attributes.site_configuration.config_type")}
            rules={[{ required: true }]}
          >
            <Select options={types} style={{ width: 200 }} />
          </Form.Item>

          {/* Only the field the type uses — the other two columns stay as they
              are, which is exactly how getConfigValue reads the row. */}
          {editedType === TYPE_NUMBER ? (
            <Form.Item
              name="number_value"
              label={t("activerecord.attributes.site_configuration.number_value")}
            >
              <InputNumber style={{ width: 200 }} />
            </Form.Item>
          ) : editedType === TYPE_DATETIME ? (
            <Form.Item
              name="datetime_value"
              label={t("activerecord.attributes.site_configuration.datetime_value")}
            >
              <DatePicker showTime style={{ width: 260 }} />
            </Form.Item>
          ) : (
            <Form.Item
              name="string_value"
              label={t("activerecord.attributes.site_configuration.string_value")}
            >
              <Input />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
}
